import { createContext, useContext, useRef, useCallback, useMemo, memo, useEffect } from 'react';
import { AudioManager } from '../hooks/useAudio.js';
import { useModalState } from '../hooks/useModalState.js';
import { useUIState } from '../hooks/useUIState.js';
import useNativeAudio from '../hooks/useNativeAudio';
import { useProgressContext } from './ProgressContext.js';
import logger from '../utils/logger';

// Create the context object.
const UIContext = createContext(null);

// Custom hook to access the UI context from any component.
export const useUIContext = () => {
    const context = useContext(UIContext);
    if (!context) {
        throw new Error('useUIContext must be used within a UIProvider');
    }
    return context;
};

// The provider component wraps the portion of the app that needs UI state.
export const UIProvider = memo(({ children }) => {
    // Use consolidated state management hooks
    const uiState = useUIState();
    const modalState = useModalState();
    const { progress } = useProgressContext();
    
    const audioManager = useRef(new AudioManager());

    // Initialize native audio hook with TTS fallback
    const nativeAudio = useNativeAudio((text, langCode) => {
        // TTS fallback function
        audioManager.current.speak(text, langCode, 1.0);
    });

    // 🔧 FIX: Sync sound settings from Firebase to AudioManager
    useEffect(() => {
        if (progress?.soundSettings) {
            logger.info('UIContext', 'Info', progress.soundSettings);
            audioManager.current.updateSettings(progress.soundSettings);
        }
    }, [progress?.soundSettings]);

    // 🔧 FIX: Sync session mute state to AudioManager
    useEffect(() => {
        logger.info('UIContext', 'Info', uiState.isSessionMuted);
        audioManager.current.setSessionMute(uiState.isSessionMuted);
    }, [uiState.isSessionMuted]);

    // History cleanup is now handled in useUIState hook

    const showFeedback = useCallback((message, type = 'success') => {
        uiState.showFeedback(message, type);
        setTimeout(() => uiState.showFeedback('', ''), 3000);
    }, [uiState]);

    // Surface native audio errors with retry prompt
    useEffect(() => {
        if (nativeAudio?.error) {
            showFeedback(nativeAudio.error, 'error');
        }
    }, [nativeAudio?.error, showFeedback]);

    const handleUserInteraction = useCallback(() => {
        if (!uiState.hasUserInteracted) {
            uiState.setHasUserInteracted(true);
            try {
                audioManager.current.start();
                logger.info('AUDIO', 'Info');
            } catch (error) {
                logger.info('AUDIO', 'Info', error);
            }
        }
    }, [uiState]);

    const handleSetPage = useCallback((newPage) => {
        uiState.setPage(newPage);
    }, [uiState]); // Dependency array is simplified

    const handleGoBack = useCallback(() => {
        logger.info('UIContext', 'Info');
        uiState.goBack();
    }, [uiState]);

    const triggerParentalGate = useCallback((onSuccess, activeProfile = null) => {
        // Check if user is 13+ and skip parental gate
        if (activeProfile && activeProfile.ageGroup === 'adult') {
            // Skip parental gate for ages 13+ (adult age group)
            onSuccess();
            return;
        }
        
        // Store the onSuccess function directly instead of wrapping it
        uiState.setGateSuccessAction(onSuccess);
        uiState.setIsGateOpen(true);
    }, [uiState]);
    
    const triggerSwitchProfile = useCallback((profile) => {
        if (profile) {
            // Stop any playing audio when switching profiles
            audioManager.current.stopAllSounds();
            modalState.setSwitchProfileModalOpen(true, profile);
        }
    }, [modalState]);

    const playVoiceOver = useCallback(async (text, langCode, rate = 1.0, audioId = null) => {
        await audioManager.current.start();
        
        // Native path is authoritative when available
        if (audioId && nativeAudio.hasNativeAudio(audioId)) {
            console.log(`🎵 Using native audio for: "${text}" (${audioId})`);
            try {
                return await nativeAudio.playAudio(text, langCode, audioId, rate);
            } catch (err) {
                logger.error('UIContext', 'Error', err);
                return;
            }
        }
        
        // Fall back to TTS only when no native is available
        console.log(`🔊 Using TTS for: "${text}"`);
        return audioManager.current.speak(text, langCode, rate);
    }, [nativeAudio]);

    const preloadNativeAudio = useCallback((audioIds = []) => {
        nativeAudio.preloadList?.(audioIds);
    }, [nativeAudio]);

    const uiContextValue = useMemo(() => ({
        // Navigation state
        page: uiState.page, 
        setPage: handleSetPage, 
        goBack: handleGoBack, 
        activeModule: uiState.activeModule, 
        setActiveModule: uiState.setActiveModule, 
        returnToUnit: uiState.returnToUnit, 
        setReturnToUnit: uiState.setReturnToUnit,
        expandedPathUnits: uiState.expandedPathUnits, 
        setExpandedPathUnits: uiState.setExpandedPathUnits,

        
        // Interaction state
        feedback: uiState.feedback, 
        showFeedback, 
        reward: uiState.reward, 
        setReward: uiState.setReward, 
        isSessionMuted: uiState.isSessionMuted, 
        toggleSessionMute: () => uiState.setIsSessionMuted(!uiState.isSessionMuted),
        hasUserInteracted: uiState.hasUserInteracted,
        
        // Onboarding state
        onboardingStep: uiState.onboardingStep, 
        setOnboardingStep: uiState.setOnboardingStep,
        onboardingData: uiState.onboardingData, 
        setOnboardingData: uiState.setOnboardingData,
        authAction: uiState.authAction, 
        setAuthAction: uiState.setAuthAction,
        
        // Gate state
        isGateOpen: uiState.isGateOpen, 
        setIsGateOpen: uiState.setIsGateOpen, 
        gateSuccessAction: uiState.gateSuccessAction, 
        triggerParentalGate,
        
        // Modal state
        isUnlockModalOpen: modalState.isUnlockModalOpen, 
        setUnlockModalOpen: modalState.setUnlockModalOpen, 
        isPaywallOpen: modalState.isPaywallOpen, 
        setPaywallOpen: modalState.setPaywallOpen,
        isCliffhangerPaywallOpen: modalState.isCliffhangerPaywallOpen,
        setCliffhangerPaywallOpen: modalState.setCliffhangerPaywallOpen,
        isTrialExpiredModalOpen: modalState.isTrialExpiredModalOpen, 
        setTrialExpiredModalOpen: modalState.setTrialExpiredModalOpen, 
        isStorybookUpsellOpen: modalState.isStorybookUpsellOpen, 
        setStorybookUpsellOpen: modalState.setStorybookUpsellOpen,
        isStreamlinedUnmuteOpen: modalState.isStreamlinedUnmuteOpen, 
        setStreamlinedUnmuteOpen: modalState.setStreamlinedUnmuteOpen, 
        showWelcomeModal: modalState.showWelcomeModal, 
        setShowWelcomeModal: modalState.setShowWelcomeModal,
        hasShownWelcomeModalThisSession: modalState.hasShownWelcomeModalThisSession, 
        setHasShownWelcomeModalThisSession: modalState.setHasShownWelcomeModalThisSession,
        isSwitchProfileModalOpen: modalState.isSwitchProfileModalOpen, 
        setIsSwitchProfileModalOpen: modalState.setSwitchProfileModalOpen,
        isProfilePickerModalOpen: modalState.isProfilePickerModalOpen, 
        setIsProfilePickerModalOpen: modalState.setProfilePickerModalOpen,
        profileToSwitchTo: modalState.profileToSwitchTo, 
        triggerSwitchProfile,
        storyCompletionFlag: modalState.storyCompletionFlag, 
        setStoryCompletionFlag: modalState.setStoryCompletionFlag,
        paywallContext: modalState.paywallContext, 
        setPaywallContext: modalState.setPaywallOpen,
        
        // Audio and interaction
        playVoiceOver, 
        audioManager: audioManager.current, 
        handleUserInteraction,
        preloadNativeAudio,
    }), [
        uiState, modalState, handleSetPage, handleGoBack, showFeedback, triggerParentalGate, 
        playVoiceOver, handleUserInteraction, triggerSwitchProfile, preloadNativeAudio
    ]);

    return (
        <UIContext.Provider value={uiContextValue}>
            {children}
        </UIContext.Provider>
    );
});
